import React from "react";
import { defaultGroupPfpUrl, defaultPfpUrl } from "../../utils";
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar";
import { Badge } from "../../ui/badge";
import { cn } from "../../../lib/utils";

/**
 * Resolve a preview image URL for a chat row (DM or group).
 * @param {{ chat_type?: string, image_url?: string, other_members?: Array<{ pfp_url?: string, pfp?: string }> }} chat
 * @returns {string}
 */
function chatPreviewImageUrl(chat) {
  if (chat.chat_type === "group" && chat.other_members?.length) {
    const first = chat.other_members[0];
    return first.pfp_url || first.pfp || chat.image_url || defaultGroupPfpUrl;
  }
  return chat.image_url || defaultPfpUrl;
}

/**
 * Collapsed floating “Messages” pill with optional avatar previews and unread badge.
 * @param {{
 *   onClick: () => void,
 *   previewChats: Array<object>,
 *   unreadCount?: number,
 *   buttonRef?: React.RefObject<HTMLButtonElement | null>,
 * }} props
 * @returns {JSX.Element}
 */
function MessageDockPill({ onClick, previewChats, unreadCount = 0, buttonRef }) {
  const previews = (previewChats || []).slice(0, 3);
  const unread = Number(unreadCount) || 0;

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onClick}
      className={cn(
        "relative flex items-center gap-2 rounded-full border border-border bg-secondary py-2 pl-3 pr-2",
        "shadow-lg transition hover:bg-chat-list-row-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
      )}
      aria-label={
        unread > 0 ? `Open messages, ${unread} unread` : "Open messages"
      }
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ui-background text-text-primary" aria-hidden="true">
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 5h12a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H8l-4 4V7a2 2 0 0 1 2-2z" />
        </svg>
      </span>
      <span className="text-sm font-semibold text-text-primary">Messages</span>
      {previews.length > 0 && (
        <span className="flex shrink-0 items-center pl-1">
          {previews.map((chat, index) => {
            const src = chatPreviewImageUrl(chat);
            const overlap = index > 0 ? "-ml-2" : "";
            return (
              <Avatar
                key={chat.id}
                className={cn("h-8 w-8 border-2 border-secondary ring-0", overlap)}
                style={{ zIndex: previews.length - index }}
              >
                <AvatarImage
                  src={src}
                  alt=""
                  onError={(e) => {
                    e.target.src = chat.chat_type === "group" ? defaultGroupPfpUrl : defaultPfpUrl;
                  }}
                />
                <AvatarFallback className="text-[10px]">
                  {(chat.name || "?").slice(0, 1).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            );
          })}
        </span>
      )}
      {unread > 0 ? (
        <Badge
          variant="default"
          className="absolute -right-1 -top-1 min-w-5 justify-center px-1.5 py-0 text-[10px] leading-4"
        >
          {unread > 99 ? "99+" : unread}
        </Badge>
      ) : null}
    </button>
  );
}

export { MessageDockPill };
